import React, { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined';

import { useAuth } from '@omnigenbiodata/ui';

import MainLayout from '../../../../layout/Main';
import ROUTES from '../../../../core/constants/routes.constants';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { setValues } from '../../../../store/batchList';
import { setValues as setCryoboxesValues } from '../../../../store/cryoboxList';
import { responseSelector as freezerResponseSelector } from '../../../../store/freezerList';
import { listFreezersThunk } from '../../../../store/freezerList';
import { BatchListFormValues } from '../../../../store/batchList/types';

import BatchSearch from './components/BatchSearch';
import StorageSearch from './components/StorageSearch';
import HomeOptions from './components/HomeOptions';

interface SearchCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  children: ReactNode;
}

interface AuthUserAttributes {
  given_name?: string;
}

interface AuthUser {
  attributes?: AuthUserAttributes;
}

function PortalBadge() {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderRadius: 999,
        bgcolor: 'primary.lighter',
        color: 'primary.main',
        border: 1,
        borderColor: 'primary.light',
        fontWeight: 700,
        fontSize: '0.95rem',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      Laboratory Portal
    </Box>
  );
}

function SearchCard({ title, description, icon, children }: SearchCardProps) {
  return (
    <Paper
      elevation={4}
      sx={{
        height: '100%',
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: { xs: 2.5, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.lighter',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
              {title}
            </Typography>

            <Typography variant="body2" component="p" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            '& form': {
              width: '100%',
            },
            '& .MuiFormControl-root': {
              mb: 2,
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Paper>
  );
}

function HomeScene() {
  const navigateTo = useNavigate();
  const dispatch = useAppDispatch();
  const freezerList = useAppSelector(freezerResponseSelector);
  const auth = useAuth();

  useEffect(() => {
    if (!freezerList) {
      dispatch(listFreezersThunk());
    }
  }, [dispatch, freezerList]);

  const handleBatchSearch = (values: BatchListFormValues) => {
    dispatch(setValues(values));
    navigateTo(ROUTES.batches);
  };

  const handleStorageSearch = (values: unknown) => {
    dispatch(setCryoboxesValues(values));
    navigateTo(ROUTES.cryobox);
  };

  const user = auth?.user as AuthUser | undefined;
  const userFirstName = user?.attributes?.given_name;

  return (
    <MainLayout showLogout>
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 5 },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            mb: 3,
            border: 1,
            borderColor: 'grey.200',
            bgcolor: 'grey.50',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <ScienceOutlined color="primary" sx={{ fontSize: 34 }} />
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  mb: 0.75,
                }}
              >
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                  Welcome back{userFirstName && `, ${userFirstName}`}
                </Typography>

                <PortalBadge />
              </Box>

              <Typography variant="body1" component="p" color="text.secondary">
                Process sample batches, manage cryobox storage, generate shipment reports and access key laboratory
                workflows.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box mb={3}>
          <HomeOptions />
        </Box>

        <Box mb={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.lighter',
                flexShrink: 0,
              }}
            >
              <ManageSearchOutlined color="primary" />
            </Box>

            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                Search
              </Typography>

              <Typography variant="body2" component="p" color="text.secondary">
                Find existing batches, cryoboxes and sample storage records.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={6}>
            <SearchCard
              title="Batch Search"
              description="Search for a sample batch using a primary ID or aliquot ID."
              icon={<SearchOutlined color="primary" />}
            >
              <BatchSearch onSearch={handleBatchSearch} stack />
            </SearchCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SearchCard
              title="Storage Search"
              description="Search by cryobox, aliquot, freezer location or biobank status."
              icon={<Inventory2Outlined color="primary" />}
            >
              <StorageSearch onSearch={handleStorageSearch} freezerList={freezerList || []} stack />
            </SearchCard>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
}

export default HomeScene;
